import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../../api/client";
import { errorMessage } from "../../cockpit/format";
import type { ApprovedCodeView, DesignationView, GradeLinkView } from "../types";

export interface NomenclatureDatabase {
  designations: DesignationView[] | null;
  gradeLinks: GradeLinkView[] | null;
  approvedCodes: ApprovedCodeView[] | null;
  loadError: string | null;
  reloadGrades: () => void;
}

export function useNomenclatureDatabase(refreshCount: number): NomenclatureDatabase {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [designations, setDesignations] = useState<DesignationView[] | null>(null);
  const [gradeLinks, setGradeLinks] = useState<GradeLinkView[] | null>(null);
  const [approvedCodes, setApprovedCodes] = useState<ApprovedCodeView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gradeReloadCount, setGradeReloadCount] = useState(0);
  const reloadGrades = useCallback(() => setGradeReloadCount((reloadCount) => reloadCount + 1), []);

  useEffect(() => {
    if (gradeReloadCount === 0) return;
    let isStale = false;
    apiFetchRef
      .current("nomenclature/grades")
      .then((response) => response.json())
      .then((loadedGradeLinks: GradeLinkView[]) => {
        if (!isStale) setGradeLinks(loadedGradeLinks);
      })
      .catch(() => undefined);
    return () => {
      isStale = true;
    };
  }, [gradeReloadCount]);

  useEffect(() => {
    let isStale = false;
    Promise.all([
      apiFetchRef.current("nomenclature/designations").then((response) => response.json()),
      apiFetchRef.current("nomenclature/grades").then((response) => response.json()),
      apiFetchRef.current("nomenclature/codes").then((response) => response.json()),
    ])
      .then(([loadedDesignations, loadedGradeLinks, loadedApprovedCodes]) => {
        if (isStale) return;
        setDesignations(loadedDesignations);
        setGradeLinks(loadedGradeLinks);
        setApprovedCodes(loadedApprovedCodes);
        setLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [refreshCount]);

  return { designations, gradeLinks, approvedCodes, loadError, reloadGrades };
}
